import { resolveListOption } from '@inference.engine/taxonomy/web';
import type { ListColumns, TaxonomyListRow } from '@/entities/taxonomy-list';

export interface CodeWithSpace {
  /** The code a binding on this list's Code column reads for this option. */
  code: string;
  /** True when the Code cell is blank and the Value stands in for it. */
  fromValue: boolean;
}

/**
 * The option's resolved code when it contains whitespace, which a schema using the list rejects.
 *
 * Resolved the way a binding on the Value and Code columns reads it: both cells trimmed, a blank
 * code replaced by the value, and an option with no value skipped, since a binding drops it. A
 * binding set to use the display value as its code reads the Value instead, which this does not
 * see.
 */
export function codeWithSpace(
  value: string | undefined,
  code: string | undefined,
): CodeWithSpace | undefined {
  const option = resolveListOption(value, code);
  if (!option || !/\s/.test(option.code)) return undefined;
  return { code: option.code, fromValue: option.codeWasBlank };
}

/** {@link codeWithSpace} for one option, read from the list's Value and Code columns. */
export function rowCodeWithSpace(
  row: TaxonomyListRow,
  columns: Pick<ListColumns, 'value' | 'code'>,
): CodeWithSpace | undefined {
  if (!columns.value) return undefined;
  return codeWithSpace(row.cells[columns.value.id], columns.code && row.cells[columns.code.id]);
}
